/**
 * Onboarding redesign (flow 2a) — palette + demo content for the three first-run screens.
 * Value + excitement only: every value here is static marketing copy / demo data, never
 * persisted or wired to a real bet, avatar save, or API. Colors mirror the approved design
 * (which was authored against our customizer surfaces — see CUSTOMIZER_SURFACE / RARITY).
 */

import { AVATAR_IDS } from '../../../constants/avatarIds';

export const ONBOARDING_COLORS = {
  bg: '#0F0A1A',
  surface: '#1F2937',
  surfaceDeep: '#111827',
  surfaceRaised: '#374151',
  border: '#374151',
  purple: '#A78BFA',
  purpleDeep: '#442698',
  purpleDeep2: '#2F1B6E',
  gold: '#FCD34D',
  gold2: '#F59E0B',
  mint: '#6EE7B7',
  green: '#065F46',
  red: '#F87171',
  redDeep: '#7F1D1D',
  ink: '#111827',
  textPrimary: '#FFFFFF',
  textSub: '#9CA3AF',
  textMuted: '#6B7280',
  textFaint: '#D1D5DB',
  pitchLine: 'rgba(167,139,250,0.10)',
} as const;

/**
 * "You" is our customizable layered avatar on the Hook + Climb screens (Climb hero + the
 * "You" leaderboard row), so the character you dress up is the one you see there.
 *
 * The Duel head-to-head card is the exception: it uses two "made" ready avatars in their
 * home/away mood states so the two characters actually face each other (home faces right,
 * away faces left) — You (left) in home, the rival (right) in away.
 */
export const ONBOARDING_YOU_AVATAR = AVATAR_IDS.GREEN_WHITE;
export const ONBOARDING_RIVAL_AVATAR = AVATAR_IDS.WHITE_RED;

/** Screen 1 — Hook. */
export const ONBOARDING_HOOK = {
  callLabel: 'YOUR CALL',
  matchup: 'Dortmund 2–1 Liverpool',
  rewardCoins: 40,
  rewardSuffix: 'if right',
  headlineAccent: 'Beat your mates.',
} as const;

export interface OnboardingPickType {
  id: string;
  label: string;
}

/** Screen 2 — Duel. */
export const ONBOARDING_DUEL = {
  rivalName: 'PitchBoss',
  youLabel: 'You',
  vsTagline: 'Same match. Winner takes the pot. 🔥',
  chipsHeader: 'DUEL ON ANY PICK TYPE',
  footer: 'Prefer a crowd? Run a weekly league with the whole group.',
  defaultPickTypeId: 'score',
  pickTypes: [
    { id: 'winner', label: 'Match Winner' },
    { id: 'score', label: 'Correct Score' },
    { id: 'ou', label: 'Over/Under 2.5' },
    { id: 'btts', label: 'Both Teams to Score' },
    { id: 'first', label: 'First to Score' },
    { id: 'double', label: 'Double Chance' },
  ] as OnboardingPickType[],
} as const;

export type OnboardingLeaderboardTone = 'green' | 'red' | 'you';

export interface OnboardingLeaderboardRow {
  rank: number;
  name: string;
  /** Monogram shown when the row has no avatar image (rivals). */
  initials?: string;
  coins: string;
  tone: OnboardingLeaderboardTone;
  /** Rank movement chip for the "You" row. */
  delta?: string;
}

/** Screen 3 — Climb. */
export const ONBOARDING_CLIMB = {
  boardLabel: 'DAILY LEADERBOARD',
  climbingLabel: "▲ You're climbing",
  gearHint: 'Kits, caps and boots unlock as you win.',
  rows: [
    { rank: 1, name: 'GoalDigger', initials: 'GD', coins: '1,420', tone: 'green' },
    { rank: 2, name: 'You', coins: '1,318', tone: 'you', delta: '▲ 3' },
    { rank: 3, name: 'PitchBoss', initials: 'PB', coins: '1,205', tone: 'red' },
  ] as OnboardingLeaderboardRow[],
} as const;
